/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */


import {Component, EventEmitter, Input, Output} from '@angular/core';

import {ButtonHubService} from './providers/button-hub';
import {WizardNavigationService} from './providers/wizard-navigation';

export const /** @type {?} */ DEFAULT_BUTTON_TYPES: any = {
    cancel: "cancel",
    previous: "previous",
    next: "next",
    finish: "finish",
    danger: "danger"
};

export const /** @type {?} */ CUSTOM_BUTTON_TYPES: any = {
    cancel: "custom-cancel",
    previous: "custom-previous",
    next: "custom-next",
    finish: "custom-finish",
    danger: "custom-danger"
};
export class WizardButton {
public type: string = "";
public disabled: boolean = false;
public hidden: boolean = false;

    // EventEmitter which is emitted when a button is clicked.
     wasClicked: EventEmitter<string> = new EventEmitter<string>(false);
/**
 * @param {?} navService
 * @param {?} buttonService
 */
constructor(public navService: WizardNavigationService,
public buttonService: ButtonHubService) {}
/**
 * @param {?=} valueToCheck
 * @param {?=} typeToLookUp
 * @return {?}
 */
private checkDefaultAndCustomType(valueToCheck: string = "", typeToLookUp: string) {
        if (DEFAULT_BUTTON_TYPES[typeToLookUp] === valueToCheck) {
            return true;
        }
        if (CUSTOM_BUTTON_TYPES[typeToLookUp] === valueToCheck) {
            return true;
        }
        return false;
    }
/**
 * @return {?}
 */
public get isCancel(): boolean {
        return this.checkDefaultAndCustomType(this.type, "cancel");
    }
/**
 * @return {?}
 */
public get isNext(): boolean {
        return this.checkDefaultAndCustomType(this.type, "next");
    }
/**
 * @return {?}
 */
public get isPrevious(): boolean {
        return this.checkDefaultAndCustomType(this.type, "previous");
    }
/**
 * @return {?}
 */
public get isFinish(): boolean {
        return this.checkDefaultAndCustomType(this.type, "finish");
    }
/**
 * @return {?}
 */
public get isDanger(): boolean {
        return this.checkDefaultAndCustomType(this.type, "danger");
    }
/**
 * @return {?}
 */
public get isPrimaryAction(): boolean {
        return this.isNext || this.isDanger || this.isFinish;
    }
/**
 * @return {?}
 */
public get _disabledAttribute(): string|null {
        if (this.isDisabled) {
            return "";
        }
        return null;
    }
/**
 * @return {?}
 */
public get isDisabled(): boolean {
        // dealing with negatives here. cognitively easier to think of it like this...
        const /** @type {?} */ disabled = true;
        const /** @type {?} */ nav = this.navService;
        const /** @type {?} */ page = this.navService.currentPage;

        // Ensure we don't change the response until buttons are ready to avoid chocolate
        if (!this.buttonService.buttonsReady) {
            return !disabled;
        }

        if (this.disabled || nav.wizardStopNavigation || !page) {
            return true;
        }

        if (this.isCancel) {
            return !disabled;
        }

        if (this.isPrevious && (nav.currentPageIsFirst || page.previousStepDisabled)) {
            return disabled;
        }

        if (this.isDanger && !page.readyToComplete) {
            return disabled;
        }

        if (this.isNext && (nav.currentPageIsLast || !page.readyToComplete)) {
            return disabled;
        }

        if (this.isFinish && (!nav.currentPageIsLast || !page.readyToComplete)) {
            return disabled;
        }

        return !disabled;
    }
/**
 * @return {?}
 */
public get isHidden(): boolean {
        // dealing with negatives here. cognitively easier to think of it like this...
        const /** @type {?} */ hidden = true;
        const /** @type {?} */ nav = this.navService;

        // Ensure we don't change the response until buttons are ready to avoid chocolate
        if (!this.buttonService.buttonsReady) {
            return !hidden;
        }

        if (this.hidden) {
            return true;
        }

        if (this.isCancel) {
            return !hidden;
        }

        if (this.isPrevious && nav.currentPageIsFirst) {
            return hidden;
        }

        if (this.isNext && nav.currentPageIsLast) {
            return hidden;
        }

        if (this.isFinish && !nav.currentPageIsLast) {
            return hidden;
        }

        return !hidden;
    }
/**
 * @return {?}
 */
click(): void {
        if (this.isDisabled) {
            return;
        }

        this.wasClicked.emit(this.type);
        this.buttonService.buttonClicked(this.type);
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-wizard-button",
    template: `
        <button
            type="button"
            class="btn clr-wizard-btn"
            [class.btn-link]="isCancel"
            [class.clr-wizard-btn--tertiary]="isCancel"
            [class.btn-outline]="isPrevious"
            [class.clr-wizard-btn--secondary]="isPrevious"
            [class.btn-primary]="isPrimaryAction"
            [class.clr-wizard-btn--primary]="isPrimaryAction"
            [class.btn-success]="isFinish"
            [class.btn-danger]="isDanger"
            [class.disabled]="isDisabled"
            [attr.disabled]="_disabledAttribute"
            (click)="click()">
            <ng-content></ng-content>
        </button>
    `,
    host: {"class": "clr-wizard-btn-wrapper", "[attr.aria-hidden]": "isHidden"},
    styles: ["[aria-hidden=\"true\"] { display: none; }"]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: WizardNavigationService, },
{type: ButtonHubService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'type': [{ type: Input, args: ["type", ] },],
'disabled': [{ type: Input, args: ["clrWizardButtonDisabled", ] },],
'hidden': [{ type: Input, args: ["clrWizardButtonHidden", ] },],
'wasClicked': [{ type: Output, args: ["clrWizardButtonClicked", ] },],
};
}

function WizardButton_tsickle_Closure_declarations() {
/** @type {?} */
WizardButton.decorators;
/**
 * @nocollapse
 * @type {?}
 */
WizardButton.ctorParameters;
/** @type {?} */
WizardButton.propDecorators;
/** @type {?} */
WizardButton.prototype.type;
/** @type {?} */
WizardButton.prototype.disabled;
/** @type {?} */
WizardButton.prototype.hidden;
/** @type {?} */
WizardButton.prototype.wasClicked;
/** @type {?} */
WizardButton.prototype.navService;
/** @type {?} */
WizardButton.prototype.buttonService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
